import React, { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, X, TicketIcon, ExternalLink, User, Building2 } from 'lucide-react';

export function TicketSearchSelect({ value, onValueChange, label = "Ticket", required = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    // Load selected ticket if value is provided
    if (value && tickets.length > 0) {
      const ticket = tickets.find(t => t.id === value);
      if (ticket) {
        setSelectedTicket(ticket);
        setSearchQuery(ticket.ticket_number + ' - ' + (ticket.title || ticket.subject || ''));
      }
    } else if (!value) {
      setSelectedTicket(null);
      setSearchQuery('');
    }
  }, [value, tickets]);

  const fetchTickets = async () => {
    try {
      const response = await axiosInstance.get('/tickets');
      setTickets(response.data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    }
  };

  useEffect(() => {
    if (searchQuery.length < 2) {
      setFilteredTickets([]);
      setShowResults(false);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    
    const results = tickets.filter(ticket => {
      const number = (ticket.ticket_number || '').toLowerCase();
      const title = (ticket.title || ticket.subject || '').toLowerCase();
      const customer = (ticket.customer_name || '').toLowerCase();
      
      return number.includes(query) || 
             title.includes(query) || 
             customer.includes(query);
    });

    // Limit to 20 results for performance
    setFilteredTickets(results.slice(0, 20));
    setShowResults(true);
  }, [searchQuery, tickets]);

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setSearchQuery(ticket.ticket_number + ' - ' + (ticket.title || ticket.subject || ''));
    setShowResults(false);
    onValueChange(ticket.id);
  };

  const handleClearTicket = () => {
    setSelectedTicket(null);
    setSearchQuery('');
    setFilteredTickets([]);
    setShowResults(false);
    onValueChange('');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // If user clears the input, clear the selection
    if (!value && selectedTicket) {
      handleClearTicket();
    }
  };

  const handleViewTicket = (e) => {
    e.stopPropagation();
    if (selectedTicket) {
      window.open(`/tickets/${selectedTicket.id}`, '_blank');
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { label: 'Urgente', class: 'bg-red-600 text-white' },
      high: { label: 'Alta', class: 'bg-orange-500 text-white' },
      medium: { label: 'Media', class: 'bg-yellow-500 text-white' },
      low: { label: 'Baja', class: 'bg-green-500 text-white' }
    };
    return config[priority] || config.medium;
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { label: 'Nuevo', class: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'En Progreso', class: 'bg-purple-100 text-purple-800' },
      waiting_tech: { label: 'Espera Técnico', class: 'bg-orange-100 text-orange-800' },
      waiting_client: { label: 'Espera Cliente', class: 'bg-yellow-100 text-yellow-800' },
      resolved: { label: 'Resuelto', class: 'bg-green-100 text-green-800' },
      closed: { label: 'Cerrado', class: 'bg-gray-100 text-gray-800' }
    };
    return config[status] || config.open;
  };

  return (
    <div className="space-y-2 relative">
      {label && (
        <Label htmlFor="ticket-search">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            id="ticket-search"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
            placeholder="Buscar por número, título o cliente..."
            className="pl-10 pr-10"
          />
          {selectedTicket && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleViewTicket}
                className="h-7 w-7 p-0 hover:bg-blue-100"
                title="Ver ticket en nueva pestaña"
              >
                <ExternalLink className="w-4 h-4 text-blue-600" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearTicket}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Search Results Dropdown */}
        {showResults && filteredTickets.length > 0 && (
          <Card className="absolute z-50 w-full mt-1 max-h-80 overflow-y-auto shadow-lg">
            <div className="p-2 space-y-1">
              {filteredTickets.map((ticket) => {
                const priority = getPriorityBadge(ticket.priority);
                const status = getStatusBadge(ticket.status);
                
                return (
                  <div
                    key={ticket.id}
                    onClick={() => handleSelectTicket(ticket)}
                    className="p-3 hover:bg-gray-100 rounded cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TicketIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                          <span className="font-mono font-semibold text-sm">{ticket.ticket_number}</span>
                          <Badge className={`${priority.class} text-xs`}>
                            {priority.label}
                          </Badge>
                        </div>
                        <div className="font-medium text-sm truncate">
                          {ticket.title || ticket.subject}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                          {ticket.customer_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {ticket.customer_name}
                            </span>
                          )}
                          {ticket.assigned_to_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {ticket.assigned_to_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge className={status.class}>
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {showResults && searchQuery.length >= 2 && filteredTickets.length === 0 && (
          <Card className="absolute z-50 w-full mt-1 shadow-lg">
            <div className="p-4 text-center text-gray-500">
              No se encontraron tickets
            </div>
          </Card>
        )}
      </div>

      {selectedTicket && (
        <div className="flex items-center gap-2">
          <div className="text-xs text-green-600 font-medium flex-1">
            ✓ Ticket seleccionado: {selectedTicket.ticket_number}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleViewTicket}
            className="h-7 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <ExternalLink className="w-3 h-3 mr-1" />
            Ver ticket
          </Button>
        </div>
      )}
    </div>
  );
}
